// @flow strict
import * as React from 'react';
import useTransition from '@ta/common.transitions';
import { Environment } from '@ta/lithium.context';
import classnames from '@ta/common.classnames';
import { ModalBase, CloseIcon, type CloseIconColorVariant } from '@ta/modal.core';
import styles from './SlideInModal.css';

/** External props for the slide in modal. */
export type Props = {|
  /** Whether the modal is open or not */
  isOpen: boolean,

  /** Direction from which to slide in from */
  slideFrom: 'left' | 'right' | 'top' | 'bottom',

  /** Optional header content. You typically want the TitleBar component. */
  header?: React.Node,

  /** Optional footer content */
  footer?: React.Node,

  /** Optional handler for overlay dismiss */
  onClose?: () => mixed,

  /** Don't show the closer X */
  enableCloseIcon?: boolean,

  /** Color to use for the close icon */
  closeIconColorVariant?: CloseIconColorVariant,

  /** The content node of the modal */
  children: React.Node,

  /** Class name to apply to the body content */
  bodyContentClass?: string,

  /** Optional time for the transition, in milliseconds */
  transitionDuration?: number,

  /** Optional flag on whether to not show the backdrop */
  hideBackdrop?: boolean,

  /** If true, focus will not automatically shift focus on open. For accessibility reasons, this value should be false */
  disableAutoFocus?: boolean,

  /** If true, events will not bubble up from the modal */
  enableEventBoundary?: boolean,

  /** ClassName that wraps header, children, footer, and close icon */
  wrapperClass?: string,
|};

const LEGACY_OVERLAY_FIX_CLASSNAME = 'layered_overlay'; // Used to prevent disableBodyScroll.es6 from swallowing touches for overlay on ios

/**
 * An overlay that slides in from a given direction. Typically used for a responsive side rail or a drawer.
 */
const SlideInModal: React.ComponentType<Props> = ({
  isOpen,
  slideFrom,
  header,
  footer,
  onClose,
  children,
  bodyContentClass,
  transitionDuration = 250,
  hideBackdrop,
  closeIconColorVariant,
  disableAutoFocus,
  enableEventBoundary,
  enableCloseIcon = true,
  wrapperClass,
}: Props) => {
  const {
    locale: { isRtl },
  } = React.useContext(Environment);
  const status = useTransition({ in: isOpen, timeout: transitionDuration });
  const directionStyle = (transitionStatus => {
    // Signifies the overlay should appear "out"
    const slideOut =
      transitionStatus === 'enter' ||
      transitionStatus === 'entering' ||
      transitionStatus === 'exiting' ||
      transitionStatus === 'exited';
    let style;
    const rightDirectionalProps = {
      top: '0',
      bottom: '0',
      right: '0',
      transform: slideOut ? 'translate3d(100%, 0, 0)' : 'translate3d(0, 0, 0)',
    };
    const leftDirectionalProps = {
      top: '0',
      bottom: '0',
      left: '0',
      transform: slideOut ? 'translate3d(-100%, 0, 0)' : 'translate3d(0, 0, 0)',
    };

    switch (slideFrom) {
      case 'top':
        style = {
          top: '0',
          left: '0',
          right: '0',
          transform: slideOut ? 'translate3d(0, -100%, 0)' : 'translate3d(0, 0, 0)',
        };
        break;
      case 'bottom':
        style = {
          bottom: '0',
          left: '0',
          right: '0',
          transform: slideOut ? 'translate3d(0, 100%, 0)' : 'translate3d(0, 0, 0)',
        };
        break;
      case 'left':
        style = isRtl ? rightDirectionalProps : leftDirectionalProps;
        break;
      case 'right':
        style = isRtl ? leftDirectionalProps : rightDirectionalProps;
        break;
      default:
        // Flow shouldn't let us get to this case.
        style = {};
    }
    return style;
  })(status);
  return (
    status !== 'exited' && (
      <ModalBase
        enableEventBoundary={enableEventBoundary}
        hideBackdrop={hideBackdrop}
        style={directionStyle}
        className={styles.modal}
        disableAutoFocus={disableAutoFocus}
        onClose={onClose}
      >
        <div
          className={classnames(styles.overlayContents, LEGACY_OVERLAY_FIX_CLASSNAME, wrapperClass)}
        >
          {header}
          <div className={classnames(styles.bodyContent, bodyContentClass)}>{children}</div>
          {footer}
          {enableCloseIcon && (
            <CloseIcon
              onClick={onClose}
              colorVariant={closeIconColorVariant}
              onEnterKeyDown={onClose}
            />
          )}
        </div>
      </ModalBase>
    )
  );
};

export default SlideInModal;
